import { z } from '@hono/zod-openapi'
import { dataEnvelope, PageLinksSchema, PgUuidSchema, ResponseMetaSchema } from './common'
import { MessagePageSchema } from './conversation-read'

export const InboxAuthorizationSchema = z
  .object({
    canViewInbox: z.boolean().openapi({
      description: 'The caller can open the Inbox: it may view conversations and some inbox.',
    }),
    access: z.array(z.string()).openapi({
      description: 'Inbox slugs the caller can open. `*` means every inbox.',
      example: ['guests', 'owners'],
    }),
  })
  .openapi('InboxAuthorization')

export const InboxAuthorizationResponseSchema = dataEnvelope(InboxAuthorizationSchema)

const SmsLineSchema = z
  .object({
    integrationId: z.string(),
    providerSlug: z.enum(['twilio', 'quo']),
    label: z.string(),
    phoneNumber: z.string().nullable(),
    isDefault: z.boolean().openapi({ description: 'New SMS messages go out on this line.' }),
  })
  .openapi('MessagingSmsLine')

export const MessagingCapabilitiesSchema = z
  .object({
    integrationSlugs: z.array(z.string()).openapi({
      description:
        'Providers of the active integrations, or none when the workspace hides ' +
        'direct-integration conversations.',
      example: ['guesty', 'twilio'],
    }),
    hasTwilioWhatsApp: z.boolean().openapi({
      description: 'A WhatsApp Business integration has an active number.',
    }),
    sms: z.object({
      hasSms: z.boolean(),
      hasTwilioSms: z.boolean(),
      hasQuoMainLineSms: z.boolean(),
      providers: z.array(SmsLineSchema),
    }),
  })
  .openapi('MessagingCapabilities')

export const MessagingCapabilitiesResponseSchema = dataEnvelope(MessagingCapabilitiesSchema)

/** Most properties one cover lookup accepts. */
export const MAX_PROPERTY_COVER_IDS = 100

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

export const PropertyCoversQuerySchema = z.object({
  ids: z
    .string()
    .openapi({
      description: `Comma-separated property ids, from 1 to ${MAX_PROPERTY_COVER_IDS}.`,
      example: '11111111-1111-4111-8111-111111111111,22222222-2222-4222-8222-222222222222',
    })
    .transform((value, ctx) => {
      const ids = [...new Set(value.split(',').map((id) => id.trim()))].filter(Boolean)
      if (ids.length === 0 || ids.length > MAX_PROPERTY_COVER_IDS) {
        ctx.addIssue({
          code: 'custom',
          message: `Send from 1 to ${MAX_PROPERTY_COVER_IDS} property ids.`,
        })
        return z.NEVER
      }
      if (!ids.every((id) => UUID.test(id))) {
        ctx.addIssue({ code: 'custom', message: 'Every property id must be a UUID.' })
        return z.NEVER
      }
      return ids
    }),
})

export const PropertyCoverSchema = z
  .object({
    propertyId: PgUuidSchema,
    coverUrl: z.string().nullable().openapi({
      description: 'The property cover image, or null when it has none.',
    }),
  })
  .openapi('PropertyCover')

export const PropertyCoversResponseSchema = z.object({
  data: z.array(PropertyCoverSchema),
  page: MessagePageSchema,
  links: PageLinksSchema,
  meta: ResponseMetaSchema,
})
